import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, FileText, Copy, Save, X, Search, Filter, 
  Brain, Download, Upload, Eye, Package, Tag, Globe, AlertTriangle,
  Calendar, User, ChevronDown, ChevronRight, Sparkles, Zap, Lightbulb
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast, ToastContainer } from '../common/Toast';
import ClauseService from '../../services/clauseService';
import MasterDataService from '../../services/masterDataService';
import requestQueue from '../../utils/requestQueue';

const ClauseLibrary = () => {
  const { success, error: showError, warning, info, toasts, removeToast } = useToast();
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit' | 'view'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClause, setSelectedClause] = useState(null);
  const [clauseToDelete, setClauseToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRisk, setSelectedRisk] = useState('');
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiGeneratedClauses, setAiGeneratedClauses] = useState([]);
  const [showRiskAnalysis, setShowRiskAnalysis] = useState(false);
  const [selectedClauseForAnalysis, setSelectedClauseForAnalysis] = useState(null);
  const [riskAnalysisResult, setRiskAnalysisResult] = useState(null);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Stats state
  const [stats, setStats] = useState({
    totalClauses: 0,
    totalCategories: 0,
    aiTemplates: 0,
    loading: true
  });
  const [masterData, setMasterData] = useState({
    categories: [],
    riskLevels: [],
    jurisdictions: [],
    languages: []
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    riskLevel: '',
    jurisdiction: '',
    language: 'en',
    tags: [],
    isActive: true,
    version: '1.0'
  });

  // Load data
  useEffect(() => {
    // Add a small delay to prevent immediate concurrent requests
    const timer = setTimeout(() => {
      loadClauses();
      loadMasterData();
      loadStats();
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const loadClauses = async (page = pagination.currentPage, limit = pagination.itemsPerPage) => {
    try {
      setLoading(true);
      
      const params = {
        page,
        limit,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedRisk && { riskLevel: selectedRisk })
      };
      
      const response = await ClauseService.getClauses(params);
      
      if (response && response.success && response.data) {
        setClauses(response.data);
        
        // Update pagination state
        if (response.pagination) {
          setPagination({
            currentPage: response.pagination.current || page,
            totalPages: response.pagination.pages || 1,
            totalItems: response.pagination.total || 0,
            itemsPerPage: limit
          });
        }
      } else {
        setClauses([]);
        setPagination(prev => ({ ...prev, totalItems: 0, totalPages: 1 }));
      }
    } catch (error) {
      console.error('Error loading clauses from API:', error);
      
      // Check if it's an authentication error
      if (error.status === 401 || error.message?.includes('Access denied') || error.message?.includes('No token provided')) {
        showError('Authentication required. Please log in to access the Clause Library.');
      } else if (error.status === 0) {
        showError('Cannot connect to server. Please check if the backend is running.');
      } else {
        showError(`Failed to load clauses: ${error.message || 'Unknown error'}`);
      }
      
      setClauses([]);
      setPagination(prev => ({ ...prev, totalItems: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStats(prev => ({ ...prev, loading: true }));
      
      // Load all clauses for stats calculation (without pagination)
      const response = await ClauseService.getClauses({ limit: 1000 });
      
      if (response && response.success && response.data) {
        const allClauses = response.data;
        
        // Calculate stats
        const totalClauses = response.pagination?.total || allClauses.length;
        const uniqueCategories = new Set(allClauses.map(c => c.category).filter(Boolean)).size;
        const aiTemplates = allClauses.filter(c => 
          c.content?.includes('{{') || 
          c.content?.includes('${') || 
          c.isAIGenerated || 
          c.source === 'ai'
        ).length;
        
        setStats({
          totalClauses,
          totalCategories: uniqueCategories,
          aiTemplates,
          loading: false
        });
      } else {
        setStats({
          totalClauses: 0,
          totalCategories: 0,
          aiTemplates: 0,
          loading: false
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      setStats({
        totalClauses: 0,
        totalCategories: 0,
        aiTemplates: 0,
        loading: false
      });
    }
  };

  const loadMasterData = async () => {
    try {
      //console.log('Loading master data from API...');
      
      // Try to load from API with delay between requests
      const [categoriesRes, riskLevelsRes, jurisdictionsRes] = await Promise.all([
        MasterDataService.getByCategory('ClauseCategory').catch(err => {
          //console.log('Failed to load categories:', err.message);
          return { success: false, data: [] };
        }),
        MasterDataService.getByCategory('RiskLevel').catch(err => {
          //console.log('Failed to load risk levels:', err.message);
          return { success: false, data: [] };
        }),
        MasterDataService.getByCategory('Jurisdiction').catch(err => {
          //console.log('Failed to load jurisdictions:', err.message);
          return { success: false, data: [] };
        })
      ]);

      setMasterData({
        categories: (categoriesRes.success && categoriesRes.data) ? categoriesRes.data : [
          { code: 'TERMINATION', label: 'Termination' },
          { code: 'LIABILITY', label: 'Liability' },
          { code: 'CONFIDENTIALITY', label: 'Confidentiality' },
          { code: 'PAYMENT', label: 'Payment' },
          { code: 'SCOPE', label: 'Scope' },
          { code: 'INTELLECTUAL_PROPERTY', label: 'Intellectual Property' },
          { code: 'COMPLIANCE', label: 'Compliance' },
          { code: 'GENERAL', label: 'General' }
        ],
        riskLevels: (riskLevelsRes.success && riskLevelsRes.data) ? riskLevelsRes.data : [
          { code: 'LOW', label: 'Low' },
          { code: 'MEDIUM', label: 'Medium' },
          { code: 'HIGH', label: 'High' },
          { code: 'CRITICAL', label: 'Critical' }
        ],
        jurisdictions: (jurisdictionsRes.success && jurisdictionsRes.data) ? jurisdictionsRes.data : [
          { code: 'US_FEDERAL', label: 'US Federal' },
          { code: 'US_STATE', label: 'US State' },
          { code: 'UK_LAW', label: 'UK Law' },
          { code: 'EU_LAW', label: 'EU Law' },
          { code: 'INDIAN_LAW', label: 'Indian Law' },
          { code: 'GLOBAL', label: 'Global' }
        ],
        languages: [
          { code: 'en', label: 'English' },
          { code: 'es', label: 'Spanish' },
          { code: 'fr', label: 'French' },
          { code: 'de', label: 'German' },
          { code: 'zh', label: 'Chinese' },
          { code: 'ja', label: 'Japanese' }
        ]
      });
      
      //console.log('Master data loaded successfully');
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };
  const loadClauseIntoForm = (clause) => {
    setFormData({
      title: clause.title || '',
      content: clause.content || '',
      category: clause.category || '',
      riskLevel: clause.riskLevel || '',
      jurisdiction: clause.jurisdiction || '',
      language: clause.language || 'en',
      tags: clause.tags || [],
      isActive: clause.isActive ?? true,
      version: clause.version || '1.0'
    });
  };

  const handleAdd = () => {
    setSelectedClause(null);
    setFormMode('add');
    setFormData({
      title: '',
      content: '',
      category: '',
      riskLevel: '',
      jurisdiction: '',
      language: 'en',
      tags: [],
      isActive: true,
      version: '1.0'
    });
    setShowAddModal(true);
  };

  const handleEdit = (clause) => { 
    setFormData({ 
      ...clause, 
      tags: clause?.tags || [] 
    }); 
    setSelectedClause(clause); 
    setShowEditModal(true); 
  };

  const handleDelete = (clause) => {
    setClauseToDelete(clause);
    setShowDeleteModal(true);
  };

  const handleSave = async () => {
    try {
      if (selectedClause) {
        await ClauseService.updateClause(selectedClause._id, formData);
        success('Clause updated successfully');
      } else {
        await ClauseService.createClause(formData);
        success('Clause created successfully');
      }
      setShowAddModal(false);
      setShowEditModal(false);
      setSelectedClause(null);
      loadClauses();
      refreshStats();
    } catch (error) {
      showError('Failed to save clause');
      console.error('Error saving clause:', error);
    }
  };

  const confirmDelete = async () => {
    try {
      await ClauseService.deleteClause(clauseToDelete._id);
      success('Clause deleted successfully');
      setShowDeleteModal(false);
      setClauseToDelete(null);
      loadClauses();
      refreshStats();
    } catch (error) {
      showError('Failed to delete clause');
      console.error('Error deleting clause:', error);
    }
  };

  const handleCopy = (clause) => {
    navigator.clipboard.writeText(clause?.content || '');
    info('Clause content copied to clipboard');
  };

  // AI Functions
  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      warning('Please enter a prompt for AI generation');
      return;
    }

    try {
      setIsGeneratingAI(true);
      
      // Enhanced AI generation with more sophisticated logic
      const generatedClauses = await generateAIClauses(aiPrompt);
      
      setAiGeneratedClauses(generatedClauses);
      success(`Generated ${generatedClauses.length} AI clause(s) successfully!`);
    } catch (error) {
      showError('Failed to generate AI clause');
      console.error('AI generation error:', error);
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Enhanced AI clause generation function
  const generateAIClauses = async (prompt) => {
    // For now, we'll use an enhanced mock system that provides more realistic clauses
    // In production, this would connect to OpenAI, Claude, or another AI service
    
    const clauseTemplates = {
      'termination': {
        title: 'Termination Clause',
        content: 'Either party may terminate this Agreement for convenience upon {{NoticeDays}} days written notice to the other party. Upon termination, {{CompanyName}} shall immediately cease all work and return any confidential information to {{ClientName}}.',
        category: 'TERMINATION',
        riskLevel: 'MEDIUM',
        jurisdiction: 'US_FEDERAL',
        language: 'en',
        tags: ['termination', 'notice', 'confidentiality']
      },
      'liability': {
        title: 'Limitation of Liability',
        content: 'Except for {{ExcludedLiabilities}}, neither party shall be liable to the other for any indirect, incidental, consequential, special, punitive or exemplary damages arising out of or in connection with this Agreement. The total liability of {{CompanyName}} shall not exceed {{MaxLiabilityAmount}}.',
        category: 'LIABILITY',
        riskLevel: 'HIGH',
        jurisdiction: 'US_FEDERAL',
        language: 'en',
        tags: ['liability', 'limitation', 'damages']
      },
      'confidentiality': {
        title: 'Confidentiality Obligation',
        content: 'Each party agrees to keep confidential all Confidential Information (as defined herein) disclosed by the other party and not to use such Confidential Information for any purpose other than as necessary to perform its obligations under this Agreement. This obligation shall survive for {{ConfidentialityPeriod}} years after termination.',
        category: 'CONFIDENTIALITY',
        riskLevel: 'MEDIUM',
        jurisdiction: 'US_FEDERAL',
        language: 'en',
        tags: ['confidentiality', 'NDA', 'survival']
      },
      'payment': {
        title: 'Payment Terms',
        content: '{{ClientName}} shall pay {{CompanyName}} the sum of {{PaymentAmount}} in accordance with the payment schedule set forth in {{ScheduleReference}}. Payment shall be due within {{PaymentDueDays}} days of receipt of invoice. Late payments shall incur interest at the rate of {{InterestRate}}% per annum.',
        category: 'PAYMENT',
        riskLevel: 'MEDIUM',
        jurisdiction: 'US_FEDERAL',
        language: 'en',
        tags: ['payment', 'terms', 'interest']
      },
      'scope': {
        title: 'Scope of Services',
        content: '{{CompanyName}} agrees to perform the services as described in the attached Statement of Work (SOW) dated {{SOWDate}}. Any changes to the scope of services must be documented in a written change order, signed by both parties, and will result in an adjustment to the {{ContractAmount}} and/or {{DeliveryDate}}.',
        category: 'SCOPE',
        riskLevel: 'MEDIUM',
        jurisdiction: 'US_FEDERAL',
        language: 'en',
        tags: ['scope', 'SOW', 'change order']
      }
    };

    // Analyze the prompt to determine the best clause type
    const promptLower = prompt.toLowerCase();
    let selectedTemplate = 'scope'; // default
    
    if (promptLower.includes('termination') || promptLower.includes('terminate')) {
      selectedTemplate = 'termination';
    } else if (promptLower.includes('liability') || promptLower.includes('damage')) {
      selectedTemplate = 'liability';
    } else if (promptLower.includes('confidential') || promptLower.includes('NDA')) {
      selectedTemplate = 'confidentiality';
    } else if (promptLower.includes('payment') || promptLower.includes('invoice')) {
      selectedTemplate = 'payment';
    } else if (promptLower.includes('scope') || promptLower.includes('service')) {
      selectedTemplate = 'scope';
    }

    const template = clauseTemplates[selectedTemplate];
    
    // Generate multiple variations
    const variations = [
      {
        ...template,
        title: `AI Generated: ${template.title}`,
        content: template.content,
        isActive: true,
        version: '1.0'
      },
      {
        ...template,
        title: `AI Generated: ${template.title} (Alternative)`,
        content: template.content.replace('{{CompanyName}}', 'the Service Provider').replace('{{ClientName}}', 'the Client'),
        isActive: true,
        version: '1.0'
      }
    ];

    // Simulate API delay
    return variations;
  };

  const handleRiskAnalysis = async (clause) => {
    try {
      setSelectedClauseForAnalysis(clause);
      setShowRiskAnalysis(true);
      
      // Simulate risk analysis (replace with actual API call)
      const mockAnalysis = {
        overallRisk: 'MEDIUM',
        riskFactors: [
          { factor: 'Liability Exposure', level: 'HIGH', description: 'High liability exposure detected' },
          { factor: 'Termination Clarity', level: 'LOW', description: 'Clear termination terms' },
          { factor: 'Payment Terms', level: 'MEDIUM', description: 'Standard payment terms' }
        ],
        recommendations: [
          'Consider adding limitation of liability clause',
          'Review termination notice periods',
          'Add force majeure provisions'
        ],
        confidence: 85
      };
      
      setRiskAnalysisResult(mockAnalysis);
      success('Risk analysis completed');
    } catch (error) {
      showError('Failed to analyze clause risk');
      console.error('Risk analysis error:', error);
    }
  };

  const handleUseAIClause = (aiClause) => {
    setFormData({
      ...aiClause,
      title: aiClause.title,
      content: aiClause.content,
      category: aiClause.category,
      riskLevel: aiClause.riskLevel,
      jurisdiction: aiClause.jurisdiction,
      language: aiClause.language,
      tags: aiClause.tags,
      isActive: true,
      version: '1.0'
    });
    setShowAIModal(false);
    setShowAddModal(true);
    info('AI clause loaded into form');
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, currentPage: newPage }));
    loadClauses(newPage, pagination.itemsPerPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, itemsPerPage: newPageSize, currentPage: 1 }));
    loadClauses(1, newPageSize);
  };

  // Search and filter handlers
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadClauses(1, pagination.itemsPerPage);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'category') {
      setSelectedCategory(value);
    } else if (filterType === 'risk') {
      setSelectedRisk(value);
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    loadClauses(1, pagination.itemsPerPage);
  };

  // Action handlers for DataTable
  const handleViewClause = (clause) => {
    setSelectedClause(clause);
    setFormMode('view');
    loadClauseIntoForm(clause);
    setShowEditModal(true);
  };

  const handleEditClause = (clause) => {
    setSelectedClause(clause);
    setFormMode('edit');
    loadClauseIntoForm(clause);
    setShowEditModal(true);
  };

  const handleDeleteClause = (clause) => {
    setClauseToDelete(clause);
    setShowDeleteModal(true);
  };

  const handleCopyClause = (clause) => {
    handleCopy(clause);
  };

  const handleRiskAnalysisClause = (clause) => {
    handleRiskAnalysis(clause);
  };

  // Refresh stats after data changes
  const refreshStats = () => {
    loadStats();
  };


  // Define columns for DataTable - Following Asset Management pattern
  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-2">
          <FileText size={16} style={{ color: 'var(--primary-color)' }} />
          <span style={{ fontWeight: '500' }}>{row?.title || 'Untitled Clause'}</span>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      render: (value, row) => (
        <span className="badge badge-secondary">
          {row?.category || 'General'}
        </span>
      )
    },
    {
      key: 'riskLevel',
      label: 'Risk',
      render: (value, row) => {
        const riskColors = {
          'LOW': 'badge-success',
          'MEDIUM': 'badge-warning',
          'HIGH': 'badge-danger',
          'CRITICAL': 'badge-danger'
        };
        return (
          <span className={`badge ${riskColors[row?.riskLevel] || 'badge-secondary'}`}>
            {row?.riskLevel || 'Medium'}
          </span>
        );
      }
    },
    {
      key: 'jurisdiction',
      label: 'Jurisdiction',
      render: (value, row) => (
        <span className="text-muted">
          {row?.jurisdiction || 'Global'}
        </span>
      )
    },
    {
      key: 'language',
      label: 'Language',
      render: (value, row) => (
        <span className="text-muted">
          {row?.language?.toUpperCase() || 'EN'}
        </span>
      )
    },
    {
      key: 'updatedAt',
      label: 'Last Updated',
      render: (value, row) => (
        <span className="text-muted">
          {row?.updatedAt ? new Date(row.updatedAt).toLocaleDateString() : 'N/A'}
        </span>
      )
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f9fafb', minHeight: '100vh' }}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0 0 8px 0',
          background: 'linear-gradient(135deg, #007B8F 0%, #4A9BA8 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          Clause Library
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          Manage legal clauses with AI-powered features and versioning
        </p>
        
      </div>

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#dbeafe',
              padding: '12px',
              borderRadius: '8px'
            }}>
              <FileText size={20} style={{ color: '#2563eb' }} />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {stats.loading ? '...' : stats.totalClauses}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Total Clauses</div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#dcfce7',
              padding: '12px',
              borderRadius: '8px'
            }}>
              <Package size={20} style={{ color: '#16a34a' }} />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {stats.loading ? '...' : stats.totalCategories}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Categories</div>
            </div>
          </div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              backgroundColor: '#fef3c7',
              padding: '12px',
              borderRadius: '8px'
            }}>
              <Brain size={20} style={{ color: '#d97706' }} />
            </div>
            <div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937' }}>
                {stats.loading ? '...' : stats.aiTemplates}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>AI Templates</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }} />
              <input
                type="text"
                placeholder="Search clauses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>

          <select
            value={selectedCategory}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="">All Categories</option>
            {masterData.categories.map(cat => (
              <option key={cat.code} value={cat.code}>{cat.label}</option>
            ))}
          </select>

          <select
            value={selectedRisk}
            onChange={(e) => handleFilterChange('risk', e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '120px'
            }}
          >
            <option value="">All Risk Levels</option>
            {masterData.riskLevels.map(risk => (
              <option key={risk.code} value={risk.code}>{risk.label}</option>
            ))}
          </select>

          <button
            onClick={() => setShowAIModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#8B5CF6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Sparkles size={16} />
            AI Generate
          </button>
          <button
            onClick={handleAdd}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            <Plus size={16} />
            Add Clause
          </button>
          
        </div>
      </div>

      {/* Clauses Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <DataTable
          data={clauses}
          columns={columns}
          title="Clause Library"
          searchable={true}
          sortable={true}
          exportable={true}
          pagination={true}
          pageSize={pagination.itemsPerPage}
          showActions={true}
          showCheckboxes={false}
          onView={handleViewClause}
          onEdit={handleEditClause}
          onDelete={handleDeleteClause}
          onCopy={handleCopyClause}
          onRiskAnalysis={handleRiskAnalysisClause}
          searchPlaceholder="Search clauses by title, category, or content..."
          emptyMessage="No clauses found. Try adjusting your search or filters."
          loading={loading}
          initialHiddenColumns={['id']}
        />
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                {showAddModal ? 'Add New Clause' : 'Edit Clause'}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  setSelectedClause(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    disabled={formMode === 'view'}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                    placeholder="Enter clause title"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    disabled={formMode === 'view'}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Category</option>
                    {masterData.categories.map(cat => (
                      <option key={cat.code} value={cat.code}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Risk Level
                  </label>
                  <select
                    value={formData.riskLevel}
                    disabled={formMode === 'view'}
                    onChange={(e) => setFormData({...formData, riskLevel: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Risk Level</option>
                    {masterData.riskLevels.map(risk => (
                      <option key={risk.code} value={risk.code}>{risk.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Jurisdiction
                  </label>
                  <select
                    value={formData.jurisdiction}
                    disabled={formMode === 'view'}
                    onChange={(e) => setFormData({...formData, jurisdiction: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="">Select Jurisdiction</option>
                    {masterData.jurisdictions.map(jur => (
                      <option key={jur.code} value={jur.code}>{jur.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Language
                  </label>
                  <select
                    value={formData.language}
                    disabled={formMode === 'view'}
                    onChange={(e) => setFormData({...formData, language: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  >
                    {masterData.languages.map(lang => (
                      <option key={lang.code} value={lang.code}>{lang.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Content * (Use {'{{placeholder}}'} for variables)
                </label>
                <textarea
                  value={formData.content}
                  disabled={formMode === 'view'}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minHeight: '200px',
                    fontFamily: 'monospace'
                  }}
                  placeholder="Enter clause content. Use {{CompanyName}}, {{NoticeDays}}, etc. for placeholders"
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    setSelectedClause(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                {formMode !== 'view' && (
                  <button
                  onClick={handleSave}
                  disabled={!formData.title || !formData.content}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: '#007B8F',
                    color: 'white',
                    cursor: 'pointer',
                    opacity: (!formData.title || !formData.content) ? 0.5 : 1
                  }}
                >
                  <Save size={16} style={{ marginRight: '8px' }} />
                    Save Clause
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Clause"
        message={`Are you sure you want to delete "${clauseToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* AI Generation Modal */}
      {showAIModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={20} style={{ color: '#8B5CF6' }} />
                AI Clause Generation
              </h2>
              <button
                onClick={() => setShowAIModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Describe the clause you want to generate:
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  minHeight: '100px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
                placeholder="e.g., 'Create a termination clause for service agreements with 30 days notice period'"
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAIModal(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  color: '#374151',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAIGenerate}
                disabled={!aiPrompt.trim() || isGeneratingAI}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  cursor: 'pointer',
                  opacity: (!aiPrompt.trim() || isGeneratingAI) ? 0.5 : 1,
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                {isGeneratingAI ? (
                  <>
                    <Brain size={16} style={{ marginRight: '8px' }} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} style={{ marginRight: '8px' }} />
                    Generate Clause
                  </>
                )}
              </button>
            </div>

            {/* Generated Clauses */}
            {aiGeneratedClauses.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>Generated Clauses:</h3>
                {aiGeneratedClauses.map((clause, index) => (
                  <div key={index} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '12px',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>{clause.title}</h4>
                      <button
                        onClick={() => handleUseAIClause(clause)}
                        style={{
                          padding: '6px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: '#007B8F',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                        }}
                      >
                        Use This Clause
                      </button>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#6b7280', lineHeight: '1.4' }}>
                      {clause.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Risk Analysis Modal */}
      {showRiskAnalysis && riskAnalysisResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Brain size={20} style={{ color: '#d97706' }} />
                AI Risk Analysis
              </h2>
              <button
                onClick={() => setShowRiskAnalysis(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '8px', fontSize: '16px', fontWeight: '600' }}>
                Clause: {selectedClauseForAnalysis?.title}
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                Overall Risk Level: <span style={{
                  padding: '4px 8px',
                  borderRadius: '4px',
                  backgroundColor: riskAnalysisResult.overallRisk === 'HIGH' ? '#fee2e2' : 
                                   riskAnalysisResult.overallRisk === 'MEDIUM' ? '#fef3c7' : '#dcfce7',
                  color: riskAnalysisResult.overallRisk === 'HIGH' ? '#dc2626' : 
                        riskAnalysisResult.overallRisk === 'MEDIUM' ? '#d97706' : '#16a34a',
                  fontWeight: '600'
                }}>
                  {riskAnalysisResult.overallRisk}
                </span>
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>Risk Factors:</h4>
              {riskAnalysisResult.riskFactors.map((factor, index) => (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '500' }}>{factor.factor}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{factor.description}</div>
                  </div>
                  <span style={{
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: factor.level === 'HIGH' ? '#fee2e2' : 
                                   factor.level === 'MEDIUM' ? '#fef3c7' : '#dcfce7',
                    color: factor.level === 'HIGH' ? '#dc2626' : 
                          factor.level === 'MEDIUM' ? '#d97706' : '#16a34a'
                  }}>
                    {factor.level}
                  </span>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '12px', fontSize: '14px', fontWeight: '600' }}>Recommendations:</h4>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {riskAnalysisResult.recommendations.map((rec, index) => (
                  <li key={index} style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Analysis Confidence: {riskAnalysisResult.confidence}%
              </span>
              <button
                onClick={() => setShowRiskAnalysis(false)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClauseLibrary;
